// Pure comparison-colour assignment (no DOM/React) — how a multi-player stats
// widget colours its columns/legend so a chip and its column read as the same
// player.
//
// Palette = Ben's hand-tuned prototype set: hues spaced ≥46° around the wheel so
// small marker dots / chips stay distinguishable at a glance (the earlier
// green/purple/teal set read as too close). A = the house green every SINGLE-
// player value already uses, so single→compare doesn't recolour the first player.

import type { PlayerRef } from "@catbento/shared";
import { refKey } from "./playerRef.js";

export const COMPARE_COLORS = [
  "#2ecc71", // A green (house)
  "#4d8bf5", // B blue
  "#f5a623", // C amber
  "#ff6b81", // D rose
  "#b06df2", // E violet
] as const;

/** The hex for a colour index (defensively clamped to the palette). */
export function colorAt(index: number): string {
  return COMPARE_COLORS[index] ?? COMPARE_COLORS[0];
}

/**
 * Stable per-player colour indices, keyed by `refKey`.
 *
 * The prototype's two rules, kept exactly:
 *  1. A colour is assigned ONCE and does NOT reshuffle when another player is
 *     removed — the survivors keep the colour they had (amber stays amber).
 *  2. A freed slot is REUSED for the next added player (lowest unused index),
 *     rather than always appending the next colour.
 *
 * Pure: given the current players + the previous map, returns the next map. It
 * keeps existing keys' indices, prunes keys no longer present, and assigns the
 * lowest-unused index to any new player (in player order). Idempotent when the
 * map already covers every player, so it doubles as a safe render-time fallback
 * for an old board that never stored colours.
 */
export function assignColors(
  players: PlayerRef[],
  prev: Record<string, number>
): Record<string, number> {
  const next: Record<string, number> = {};
  const used = new Set<number>();

  // Keep the colour any still-present player already had (no reshuffle).
  for (const p of players) {
    const k = refKey(p);
    if (prev[k] !== undefined) {
      next[k] = prev[k];
      used.add(prev[k]);
    }
  }
  // Assign the lowest unused index to any new player, in player order.
  for (const p of players) {
    const k = refKey(p);
    if (next[k] === undefined) {
      let i = 0;
      while (used.has(i)) i += 1;
      next[k] = i;
      used.add(i);
    }
  }
  return next;
}
